<template>
  <v-container
    :style="containerStyles"
    class="pa-4 pt-0 checklist-form-container"
    fluid
  >
    <div class="sections-list">
      <ChecklistSection
        v-for="(section, sectionKey, index) in pkcData.sections"
        :key="sectionKey"
        :available-values="availableValues"
        :data-section="sectionKey"
        :enabled="true"
        :process-info="processInfo"
        :readonly="isReadOnly"
        :section="section"
        :section-index="index"
        :section-key="sectionKey"
        @update-parameter-value="(paramKey, val) => updateParameterValue(sectionKey, paramKey, val)"
        @update-parameter-comment="(paramKey, val) => updateParameterComment(sectionKey, paramKey, val)"
      />
    </div>
  </v-container>
</template>

<script>
  import ChecklistSection from '@/components/management/processManagement/components/checklist/ChecklistSection.vue'
  import '@/components/management/processManagement/components/checklist/checkMethods'
  import vuex_authentication from '@/store/modules/vuex_authentication'

  export default {
    name: 'PMChecklistForm',
    components: {
      ChecklistSection
    },
    props: {
      processInfo: {
        type: Object,
        default: null
      },
      data: {
        type: Object,
        default: null
      },
      versionStatus: {
        type: String,
        default: 'draft'
      },
      fromView: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        availableValues: ['pending', 'checked', 'not_required'],
        pkcData: this.data?.metadata?.values,
        saving: false
      }
    },
    computed: {
      isReadOnly() {
        return this.versionStatus !== 'draft'
      },
      containerStyles() {
        // Calcular la altura máxima basada en el contexto
        const baseHeight = this.fromView ? '85vh' : 'calc(100dvh - 220px)'
        const fallbackHeight = this.fromView ? '85vh' : 'calc(100vh - 160px)'

        return {
          maxHeight: baseHeight,
          // Fallback para navegadores sin soporte dvh
          '@supports not (height: 100dvh)': {
            maxHeight: fallbackHeight
          }
        }
      }
    },
    methods: {
      updateParameterValue(sectionKey, paramKey, newValue) {
        if (this.isReadOnly) return

        // Caso especial para actualizar el valor de la sección directamente
        if (paramKey === '_section') {
          const section = this.pkcData.sections[sectionKey]
          this.$set(section, 'value', newValue)
          this.$set(section, 'timestamp', this.$moment().unix())

          // Agregar user a la sección para mejor trazabilidad
          if (this.$store.state.vuex_authentication?.username) {
            this.$set(section, 'user', this.$store.state.vuex_authentication.username)
          }

          this.emitDataUpdate()
          return
        }

        // Caso normal para actualizar parámetros
        const parameter = this.pkcData.sections[sectionKey].parameters[paramKey]
        this.$set(parameter, 'value', newValue)

        if (newValue === 'checked' || newValue === 'not_required') {
          this.$set(parameter, 'timestamp', this.$moment().unix())
          this.$set(parameter, 'user', this.$store.state[vuex_authentication.constants.NAMESPACE][vuex_authentication.constants.S_USERNAME])

          // Actualizar valores en extraInfo con valores actuales del proceso
          if (parameter.extraInfo && this.processInfo) {
            if (!parameter.extraInfo) {
              this.$set(parameter, 'extraInfo', {})
            }

            Object.keys(parameter.extraInfo).forEach(extraKey => {
              const processValue = this.getProcessValue(extraKey)
              if (processValue !== null) {
                this.$set(parameter.extraInfo, extraKey, processValue)
              }
            })
          }

          this.checkSectionStatus(sectionKey)
        }

        this.emitDataUpdate()
      },

      updateParameterComment(sectionKey, paramKey, comment) {
        if (this.isReadOnly) return
        this.pkcData.sections[sectionKey].parameters[paramKey].comment = comment
        this.emitDataUpdate()
      },

      emitDataUpdate() {
        if (this.isReadOnly) return
        this.$emit('checklist-data-updated', this.pkcData)
      },

      getProcessValue(field) {
        if (!this.processInfo || !field) return 'N/A'
        const value = field.split('.').reduce((obj, key) => obj && obj[key], this.processInfo)
        return value !== undefined ? value : 'N/A'
      },

      checkSectionStatus(sectionKey) {
        if (!this.pkcData.sections[sectionKey]) return

        const section = this.pkcData.sections[sectionKey]
        const parameters = section.parameters

        const allCompleted = Object.values(parameters).every(param =>
          param.value === 'checked' || param.value === 'not_required'
        )

        if (allCompleted && section.value === 'pending') {
          section.value = 'checked'
          section.timestamp = this.$moment().unix()
          this.emitDataUpdate()
        }
      },

      async saveChecklist() {
        if (this.isReadOnly) return

        try {
          this.saving = true

          const updatedMetadata = {
            ...this.data.metadata,
            values: this.pkcData,
            lastUpdated: this.$moment().unix()
          }

          await this.$parent.updateMetadataFile(updatedMetadata)
          this.data.metadata = updatedMetadata
          this.$toast?.success?.('Checklist guardado correctamente')

        } catch (error) {
          this.$toast?.error?.('Error al guardar el checklist')
        } finally {
          this.saving = false
        }
      },

      validateChecklist() {
        // Implementar lógica de validación
      },

      scrollToParameter(sectionKey, paramKey) {
        this.$nextTick(() => {
          const paramElement = document.querySelector(`[data-section="${sectionKey}"] [data-parameter="${paramKey}"]`)

          if (paramElement) {
            const sectionElement = document.querySelector(`[data-section="${sectionKey}"]`)
            if (sectionElement) {
              const sectionComponents = this.$children.filter(child =>
                child.$options.name === 'ChecklistSection' && child.sectionKey === sectionKey
              )

              if (sectionComponents.length > 0) {
                if (!sectionComponents[0].expanded) {
                  sectionComponents[0].expanded = true
                }

                setTimeout(() => {
                  paramElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  paramElement.classList.add('parameter-highlight-background')

                  setTimeout(() => {
                    paramElement.classList.remove('parameter-highlight-background')
                  }, 2000)
                }, 300)
              } else {
                paramElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                paramElement.classList.add('parameter-highlight-background')

                setTimeout(() => {
                  paramElement.classList.remove('parameter-highlight-background')
                }, 2000)
              }
            } else {
              paramElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
              paramElement.classList.add('parameter-highlight-background')

              setTimeout(() => {
                paramElement.classList.remove('parameter-highlight-background')
              }, 2000)
            }
          }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/styles/resources/colors.scss";
  @import "@/assets/styles/resources/variables.scss";

  .checklist-form-container {
    overflow: auto;

    // Aplicar estilos dinámicos para navegadores con soporte dvh
    @supports (height: 100dvh) {
      &[style*="80vh"] {
        max-height: 80vh !important;
      }
    }

    // Fallback para navegadores sin soporte dvh
    @supports not (height: 100dvh) {
      &[style*="80vh"] {
        max-height: 80vh !important;
      }
    }
  }

  .process-info-card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 16px;

    .process-info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;

      .info-item {
        display: flex;
        flex-direction: column;

        .info-label {
          font-size: 12px;
          color: #666;
          margin-bottom: 4px;
        }

        .info-value {
          font-weight: 500;
          color: #333;
        }
      }
    }
  }

  .config-section {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 16px;
  }

  @keyframes highlight-param {
    0% {
      background-color: rgba(255, 152, 0, 0.2) !important;
    }
    50% {
      background-color: rgba(255, 152, 0, 0.3) !important;
    }
    100% {
      background-color: transparent;
    }
  }

  @keyframes parameter-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
      transform: scale(1);
      background-color: rgba(255, 152, 0, 0.3);
    }
    40% {
      box-shadow: 0 0 0 10px rgba(255, 152, 0, 0);
      transform: scale(1.02);
      background-color: rgba(255, 152, 0, 0.2);
    }
    80% {
      box-shadow: 0 0 0 10px rgba(255, 152, 0, 0);
      transform: scale(1);
      background-color: rgba(255, 152, 0, 0.1);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
      transform: scale(1);
      background-color: transparent;
    }
  }

  .parameter-highlight {
    animation: highlight-param 2s ease;
  }

  .parameter-highlight-pulse {
    animation: parameter-pulse 3s ease-in-out;
    border: 1px solid rgba(255, 152, 0, 0.5) !important;
    z-index: 1;
    position: relative;

    .section-title {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 12px;
      color: #333;
      display: flex;
      align-items: center;
    }

    .config-items {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .config-item {
        cursor: pointer;
      }
    }
  }

  .sections-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .section-block {
    background: white;
    border: none;
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: #f5f5f5;

    .section-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .section-icon {
        color: #666;
      }

      .section-name {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 4px 0;
        color: #333;
      }

      .section-description {
        font-size: 13px;
        color: #666;
        margin: 0;
      }
    }

    .section-status {
      .status-disabled {
        background: #f5f5f5;
        color: #999;
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 12px;
        border: 1px solid #ddd;
      }

      .status-progress {
        background: #e3f2fd;
        color: #1976d2;
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 16px !important;
        font-weight: 500;
      }
    }
  }

  .section-content {
    padding: 16px;

    &.content-disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  @media (max-width: 768px) {
    .process-info-grid {
      grid-template-columns: 1fr;
    }

    .section-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
  }

  @keyframes parameter-highlight-fade {
    0% {
      background-color: rgba(255, 193, 7, 0.5) !important;
      transform: scale(1.02);
    }
    50% {
      background-color: rgba(255, 193, 7, 0.4) !important;
      transform: scale(1.01);
    }
    100% {
      background-color: rgba(255, 193, 7, 0.2) !important;
      transform: scale(1);
    }
  }

  .parameter-highlight-animated {
    animation: parameter-highlight-fade 2s ease-out forwards !important;
    border: 2px solid rgba(255, 193, 7, 0.6) !important;
    border-radius: 4px !important;
  }
</style>